'use client'

import { useCallback, useEffect, useState } from 'react'
import { AddIcon } from '@repo/ui/AddIcon'
import { CrossIcon } from '@repo/ui/CrossIcon'
import { WorktypeSelect } from '@repo/ui/WorktypeSelect'
import { FormCard } from '@repo/form-components/FormCard'
import { useFormsStore } from '@repo/store/useFormsStore'
import { confirm } from '@repo/ui/Confirm'
import MUICard from '@mui/material/Card'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIIconButton from '@mui/material/IconButton'
import MUILink from '@mui/material/Link'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import { LinkFormsModal } from '../LinkFormsModal/LinkFormsModal'

type WorktypeOption = {
  workflowTypeId: string
  description?: string
} | null

type FormWorkTypeMapping = {
  formWorkTypeId: string
  formId: string
  workTypeId: string
}

type Form = {
  formId?: string
  id?: string
  name?: string
  description?: string
  formCategoryId?: string
}

type LinkedRow = {
  formWorkTypeId: string
  formId: string
  name: string
  description: string
  formCategoryId?: string
}

const extractRows = <T,>(response: unknown): T[] => {
  if (Array.isArray(response)) return response as T[]
  const wrapped = response as { rows?: T[]; items?: T[] } | null | undefined
  return (wrapped?.rows ?? wrapped?.items ?? []) as T[]
}

export const WorktypeLinkingPanel = () => {
  const { getAllFormWorkTypes, getAllForms, deleteFormWorkType, fetchAllFormCategories } = useFormsStore()

  const [selectedWorktype, setSelectedWorktype] = useState<WorktypeOption>(null)
  const [linkedRows, setLinkedRows] = useState<LinkedRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    fetchAllFormCategories()
  }, [fetchAllFormCategories])

  const workTypeId = selectedWorktype?.workflowTypeId ?? null
  const workTypeLabel = selectedWorktype?.description ?? ''
  const worktypeSelected = Boolean(workTypeId)

  const refresh = useCallback(async () => {
    if (!workTypeId) {
      setLinkedRows([])
      setHasError(false)
      return
    }
    setIsLoading(true)
    setHasError(false)
    try {
      const [mappingsResponse, formsResponse] = await Promise.all([getAllFormWorkTypes(), getAllForms()])

      const mappings = extractRows<FormWorkTypeMapping>(mappingsResponse).filter(
        (row) => String(row.workTypeId) === String(workTypeId)
      )
      const forms = extractRows<Form>(formsResponse)
      const formsById = new Map<string, Form>(forms.map((form) => [String(form.formId ?? form.id ?? ''), form]))

      const rows: LinkedRow[] = mappings.map((mapping) => {
        const form = formsById.get(String(mapping.formId))
        return {
          formWorkTypeId: String(mapping.formWorkTypeId),
          formId: String(mapping.formId),
          name: form?.name ?? '',
          description: form?.description ?? '',
          formCategoryId: form?.formCategoryId
        }
      })

      setLinkedRows(rows)
    } catch {
      setHasError(true)
      setLinkedRows([])
    } finally {
      setIsLoading(false)
    }
  }, [workTypeId, getAllFormWorkTypes, getAllForms])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleUnlink = async (formWorkTypeId: string) => {
    const proceed = await confirm('Are you sure you want to unlink this Form?')
    if (!proceed) return
    await deleteFormWorkType({ formWorkTypeId })
    await refresh()
  }

  const existingFormIds = linkedRows.map((row) => row.formId)

  return (
    <MUICard
      data-testid="FormEvoWorktypeLinkingPanel"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        p: 8,
        maxHeight: '40rem',
        maxWidth: 800,
        width: '100%',
        mx: 'auto',
        mt: '3rem',
        backgroundColor: 'common.white'
      }}
    >
      <MUIStack gap={0.5}>
        <MUITypography variant="body1" sx={{ fontWeight: 600 }}>
          Use this screen to link FormEvo forms to worktypes.
        </MUITypography>
        <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
          Makes it easier to find the right forms within the Matter.
        </MUITypography>
      </MUIStack>

      <MUIStack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <WorktypeSelect
          value={selectedWorktype}
          onChange={(val: WorktypeOption) => setSelectedWorktype(val)}
          sx={{ maxWidth: 320 }}
        />
        <MUILink
          component="button"
          type="button"
          onClick={() => setIsModalOpen(true)}
          disabled={!worktypeSelected}
          underline="always"
          color="secondary"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            background: 'none',
            border: 0,
            p: 0,
            cursor: worktypeSelected ? 'pointer' : 'not-allowed',
            opacity: worktypeSelected ? 1 : 0.5
          }}
        >
          <AddIcon sx={{ fontSize: '1rem' }} />
          Link forms
        </MUILink>
      </MUIStack>

      {worktypeSelected && isLoading && (
        <MUIStack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
          <MUICircularProgress />
        </MUIStack>
      )}

      {worktypeSelected && !isLoading && hasError && (
        <MUITypography variant="body2" sx={{ color: 'error.main' }}>
          Unable to load forms for this worktype. Please try again.
        </MUITypography>
      )}

      {worktypeSelected && !isLoading && !hasError && linkedRows.length === 0 && (
        <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
          No forms linked to this worktype yet.
        </MUITypography>
      )}

      {worktypeSelected && !isLoading && !hasError && linkedRows.length > 0 && (
        <MUIStack gap={2} sx={{ overflowY: 'scroll' }}>
          {linkedRows.map((row) => (
            <MUIStack key={row.formWorkTypeId} direction="row" alignItems="center" gap={1}>
              <MUIStack sx={{ flex: 1, minWidth: 0 }}>
                <FormCard
                  variant="display"
                  formId={row.formId}
                  name={row.name}
                  description={row.description}
                  formCategoryId={row.formCategoryId}
                />
              </MUIStack>
              <MUIIconButton
                aria-label="Unlink form"
                onClick={() => handleUnlink(row.formWorkTypeId)}
                sx={{ flexShrink: 0 }}
              >
                <CrossIcon sx={{ fontSize: '16px', color: '#3D517B' }} />
              </MUIIconButton>
            </MUIStack>
          ))}
        </MUIStack>
      )}

      {worktypeSelected && workTypeId && (
        <LinkFormsModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          workTypeId={workTypeId}
          workTypeLabel={workTypeLabel}
          existingFormIds={existingFormIds}
          onSaved={refresh}
        />
      )}
    </MUICard>
  )
}
